import {memo, useEffect, useMemo, useState} from 'react'
import {useTranslation} from '@/translations/i18n'
import {ModalOverlay, ModalXCloseButton} from '@/components/ui/modal-atoms/ModalAtoms.tsx'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {usePermissions} from '@/features/auth/store/store'
import {ALL_ENTITIES, ExportFormat, ExportStatusFilter, ExportTabSelection} from '@/features/exports/types'
import {getPermittedEntityDescriptors} from '@/features/exports/utils/entities'
import {useLastExportTab} from '@/features/exports/hooks/useLastExportTab'
import {ExportsEntitySelect} from '@/features/exports/components/exports-entity-select/ExportsEntitySelect'
import {ExportsFilters} from '@/features/exports/components/exports-filters/ExportsFilters'
import {ExportsList} from '@/features/exports/components/exports-list/ExportsList'
import {StyledExportsModal, StyledExportsModalBody, StyledExportsModalHeader} from './style'

interface ExportsModalProps {
    isOpen: boolean
    onClose: () => void
}

const ExportsModalComponent = ({isOpen, onClose}: ExportsModalProps) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const descriptors = useMemo(() => getPermittedEntityDescriptors(permissions), [permissions])
    const {resolveInitialTab, persistTab} = useLastExportTab(descriptors)

    const [activeSelection, setActiveSelection] = useState<ExportTabSelection | null>(null)
    const [status, setStatus] = useState<ExportStatusFilter | undefined>(undefined)
    const [format, setFormat] = useState<ExportFormat | undefined>(undefined)
    const [dateFrom, setDateFrom] = useState<string | undefined>(undefined)
    const [dateTo, setDateTo] = useState<string | undefined>(undefined)
    const [includeArchived, setIncludeArchived] = useState(false)

    // Resolve the tab to open with (last-viewed, else all entities) on each open.
    useEffect(() => {
        if (isOpen) {
            setActiveSelection(prev => prev ?? resolveInitialTab())
        } else {
            setActiveSelection(null)
            setStatus(undefined)
            setFormat(undefined)
            setDateFrom(undefined)
            setDateTo(undefined)
            setIncludeArchived(false)
        }
    }, [isOpen, resolveInitialTab])

    const handleSelectEntity = (selection: ExportTabSelection) => {
        setActiveSelection(selection)
        persistTab(selection)
    }

    if (!isOpen) return null

    const showAllEntities = activeSelection === ALL_ENTITIES
    const activeDescriptor = descriptors.find(descriptor => descriptor.key === activeSelection)
    // The data type is "active" (and shown as a removable chip) only when a specific entity is picked.
    const dataTypeLabel = !showAllEntities && activeDescriptor ? t(activeDescriptor.labelKey) : undefined
    const params = {
        entity: activeSelection && !showAllEntities ? activeSelection : undefined,
        status,
        format,
        dateFrom,
        dateTo,
        includeArchived
    }

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <StyledExportsModal width="520px" onClick={event => event.stopPropagation()}>
                <StyledExportsModalHeader direction="column" gap={3} fullWidth>
                    <Flexbox justify="space-between" align="center" fullWidth>
                        <Text size="textXl" palette="neutral" shade="900" weight="500">
                            {t('exports:title')}
                        </Text>
                        <ModalXCloseButton onClick={onClose} />
                    </Flexbox>

                    {activeSelection && (
                        <ExportsEntitySelect
                            descriptors={descriptors}
                            activeSelection={activeSelection}
                            onSelect={handleSelectEntity}
                        />
                    )}

                    <ExportsFilters
                        dataTypeLabel={dataTypeLabel}
                        onDataTypeClear={() => handleSelectEntity(ALL_ENTITIES)}
                        status={status}
                        onStatusChange={setStatus}
                        format={format}
                        onFormatChange={setFormat}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onDateFromChange={setDateFrom}
                        onDateToChange={setDateTo}
                        includeArchived={includeArchived}
                        onIncludeArchivedChange={setIncludeArchived}
                    />
                </StyledExportsModalHeader>

                <StyledExportsModalBody>
                    {/* Always show the type label as each row's title — it stays meaningful even when a
                        single "Tipo di report" is selected (e.g. an activity report's auto-named file). */}
                    {activeSelection && <ExportsList params={params} showEntity />}
                </StyledExportsModalBody>
            </StyledExportsModal>
        </>
    )
}

export const ExportsModal = memo(ExportsModalComponent)
